/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by kyang on 4/8/19.
 */

function EchoServiceConstructor(
    this: JS5Object,
    _: JS5Object,
    $http: JS5Function,
    $q: JS5Object,
    $window: JS5Object,
    loginService: JS5Object,
    EndPointService: JS5Object,
    NotificationService: JS5Object,
    RequestService:  JS5Object
) {
    let dataRefreshInterval: JS5Object;
    // Default, should only be used in unit tests.
    this.serverStatusRefreshEnabled = _.get($window, '__wfcConfig.serverStatusRefreshEnabled', true);
    this.serverStatusRefreshDelay = _.get($window, '__wfcConfig.serverStatusRefreshDelay', 5);

    this.getRefreshDelayMillis = () => {
        return this.serverStatusRefreshDelay * 1000;
    };

    /* Handle callbacks */
    this.registerDataUpdateCallback = (callbackFunc: JS5Function) => {
        if (typeof(callbackFunc) == 'function') {
            if (!_.includes(this.dataUpdateCallbacks, callbackFunc)) {
                this.dataUpdateCallbacks.push(callbackFunc);
                callbackFunc(this.serverStatus); // Send current value
            }
        }
    };

    this.unregisterDataUpdateCallback = (callbackFunc: JS5Function) => {
        _.pull(this.dataUpdateCallbacks, callbackFunc);
    };

    /* Functions to start & stop updating status every so often */
    this.activateDataRefreshInterval = () => {
        const deferred = $q.defer();
        if (!dataRefreshInterval) {
            dataRefreshInterval = RequestService.createRequestInterval(updateStatus, this.getRefreshDelayMillis(), {startImmediately: true});
        }

        if (this.serverStatus.response === undefined) {
            const resolveCallback: JS5Function = (newServerStatus) => {
                if (_.get(newServerStatus, 'response')) {
                    deferred.resolve(newServerStatus);
                    this.unregisterDataUpdateCallback(resolveCallback);
                }
            };
            this.registerDataUpdateCallback(resolveCallback);

        } else {
            deferred.resolve(this.serverStatus);
        }
        return deferred.promise;
    };

    this.deactivateDataRefreshInterval = () => {
        if (dataRefreshInterval) {
            RequestService.cancelRequestInterval(dataRefreshInterval);
        }
        dataRefreshInterval = undefined;
    };

    enum EventStatus {
        PENDING,
        SUCCEEDED, // Show info notification
        FAILED,    // Show error notification
    }
    this.EventStatus = EventStatus;

    // parseEventFn accepts 1 arg: Event DTO. Returns object with 2 keys: status & message
    this.awaitNotification = (id: string, parseEventFn: JS5Function) => {
        this.pendingNotifications.push({id, parseEventFn});
    };

    // Save status, & report new status to all callbacks
    const updateStatus = () => {
        return this.getServerStatus().then(
            (serverStatus: JS5Object) => {
                this.serverStatus = serverStatus;
                // Clone callbacks so outside functions can't mess with the forEach loop
                // Example: One of the callbacks can unregister itself when triggered
                _.forEach(_.clone(this.dataUpdateCallbacks), (callback: JS5Function) => {
                    callback(this.serverStatus);
                });
            }
        );
    };

    // Try to connect to the server
    this.getServerStatus = () => {
        let statusPromise: JS5Promise;
        if (loginService.isAuthenticated() && this.pendingNotifications.length > 0) {
            statusPromise = this.getEventNotifications();
        } else if (this.serverStatusRefreshEnabled) {
            statusPromise = $http({
                method: 'GET',
                url: EndPointService.echoEndPoint
            });
        } else {
            statusPromise = $q.when({
                status: 200,
                statusText: 'OK',
                data: {}
            });
        }

        // Convert success & failure into an object
        return statusPromise.then(
            (successResponse: any) => {
                return {serverResponded: true, response: _.pick(successResponse, ['status', 'statusText', 'data'])};
            },
            (failResponse: any) => {
                return {serverResponded: false, response: _.pick(failResponse, ['status', 'statusText', 'data'])};
            }
        );
    };

    // Get event notifications & display them. Expects at least 1 pendingNotification to exist
    this.getEventNotifications = () => {
        // Make requests & parse them
        const eventRequests = this.pendingNotifications.map((eventInfo: {id: string, parseEventFn: JS5Function}) => {
            return $http({
                method: 'GET',
                url: EndPointService.eventNotificationsEndPoint +'/'+ eventInfo.id,
            }).then((eventResponse: any) => {
                return {
                    ...eventInfo.parseEventFn(eventResponse.data),
                    eventResponse
                };
            }, (failResponse: any) => {
                return {eventResponse: failResponse};
            });
        });

        // Show notifications & update pendingNotifications
        return $q.all(eventRequests).then((parsedEventsInfo: any[]) => {
            let pingResponse = undefined;
            this.pendingNotifications = this.pendingNotifications.filter((eventInfo: any, index: number) => {
                const parsedEventInfo = parsedEventsInfo[index];
                if (!parsedEventInfo) {
                    return true;
                } else if (parsedEventInfo.status === undefined) {
                    pingResponse = parsedEventInfo.eventResponse;
                    return true;
                } else if (parsedEventInfo.status === EventStatus.SUCCEEDED) {
                    NotificationService.createPersistentInfoNotification(parsedEventInfo.message);
                    return false;
                } else if (parsedEventInfo.status === EventStatus.FAILED) {
                    NotificationService.createFailureNotification(parsedEventInfo.message, '', true, -1);
                    return false;
                } else {
                    return true;
                }
            });

            if (pingResponse) {
                return $q.reject(pingResponse);
            }
            return parsedEventsInfo[0].eventResponse;
        });
    };

    let disconnectNotification: JS5Object;
    const notificationCallback: JS5Function = (serverStatus) => {
        if (serverStatus.serverResponded || !loginService.isAuthenticated()) {
            if (!!disconnectNotification) {
                disconnectNotification.close();
                disconnectNotification = undefined;
            }
        // No response, and logged in, check next case
        } else if (!!serverStatus.response && !disconnectNotification) {
            disconnectNotification = NotificationService.createFailureNotification('Got disconnected from the server',
                '',
                false,
                -1
            );
        }
    };

    this.initialize = () => {
        if (!this.dataUpdateCallbacks) {
            this.pendingNotifications = [];
            this.serverStatus = {serverResponded: false, response: undefined};
            this.dataUpdateCallbacks = [notificationCallback];
        }
        return this.activateDataRefreshInterval();
    }
}

angular
    .module('prism-ui.components.data')
    .service('EchoService', EchoServiceConstructor);
